import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Card, Space, Divider, message } from 'antd';
import { PhoneOutlined, SyncOutlined, CustomerServiceOutlined, MessageOutlined, EllipsisOutlined } from '@ant-design/icons';
import { agentService } from '../services/api';
import { useSoftphone } from '../context/SoftphoneContext';
import { Tooltip, Button, Dropdown } from 'antd';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const AgentsPage = () => {
    const { makeCall, status: softphoneStatus, inCall, isCalling } = useSoftphone();
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];

    const softphoneEnabled = softphoneStatus === 'registered';
    const canGlobalCall = softphoneEnabled && !inCall && !isCalling;
    const canListen = permissions.includes('agents.listening');

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [isUpdating, setIsUpdating] = useState(false);

    const fetchData = async (isInitial = false) => {
        if (isInitial) setLoading(true);
        setIsUpdating(true);
        try {
            const response = await agentService.getMonitoring();
            if (response.status === 'success') {
                setData(response.data);
            }
        } catch (error) {
            console.error("Error fetching agent data", error);
            if (isInitial) message.error("No se pudo cargar la información de agentes");
        } finally {
            if (isInitial) setLoading(false);
            setLastUpdate(new Date());
            setTimeout(() => setIsUpdating(false), 1000);
        }
    };

    useEffect(() => {
        fetchData(true);
        const interval = setInterval(() => fetchData(), 2000);
        return () => clearInterval(interval);
    }, []);

    const formatTime = (seconds) => {
        if (!seconds) return '00:00:00';
        seconds = parseInt(seconds, 10);
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return [h, m, s].map(v => v < 10 ? '0' + v : v).join(':');
    };

    const getStatusTag = (status) => {
        const statuses = {
            'available': { color: 'success', text: 'Disponible' },
            'online': { color: 'processing', text: 'Conectado' }, // Azul
            'incall': { color: 'error', text: 'En Llamada' }, // Rojo
            'ringing': { color: '#cf1322', text: 'Timbrando', custom: true }, // Rojo oscuro
            'break': { color: 'warning', text: 'Break' },
            'wrapup': { color: 'warning', text: 'Esperando Formulario' },
            'offline': { color: 'default', text: 'Desconectado' }
        };
        const st = statuses[status] || statuses['offline'];

        if (st.custom) {
            return <Tag color={st.color} style={{ fontWeight: 'bold', color: '#fff', borderColor: st.color, background: st.color }}>{st.text.toUpperCase()}</Tag>;
        }
        return <Tag color={st.color} style={{ fontWeight: 'bold' }}>{st.text.toUpperCase()}</Tag>;
    };

    const columns = [
        {
            title: 'Agente',
            key: 'agent',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.name}</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>@{record.username}</Text>
                </Space>
            )
        },
        {
            title: 'Extensión',
            dataIndex: 'sip_extension',
            key: 'sip_extension',
            render: (text) => {
                if (!text) return '-';
                try {
                    const sipData = JSON.parse(text);
                    return <Text code>{sipData.extension || '-'}</Text>;
                } catch (e) {
                    return <Text code>{text}</Text>;
                }
            }
        },
        {
            title: 'Estado Actual',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Llamada Activa',
            key: 'active_call',
            render: (_, record) => {
                if (!record.active_phone) return <Text type="secondary" style={{ fontStyle: 'italic' }}>Sin llamada</Text>;
                return (
                    <Space direction="vertical" size={0}>
                        <Space>
                            <PhoneOutlined style={{ color: '#52c41a' }} />
                            <Text strong>{record.active_phone}</Text>
                        </Space>
                        <Tag color="blue" size="small" style={{ fontSize: '10px' }}>{record.active_campaign}</Tag>
                    </Space>
                );
            }
        },
        {
            title: 'Métricas del Día (KPIs)',
            key: 'kpis',
            render: (_, record) => (
                <Space split={<Divider type="vertical" />}>
                    <Space direction="vertical" size={0} align="center">
                        <Text strong style={{ fontSize: '16px' }}>{record.total_calls}</Text>
                        <Text type="secondary" style={{ fontSize: '10px' }}>LLAMADAS</Text>
                    </Space>
                    <Space direction="vertical" size={0} align="center">
                        <Text strong style={{ fontSize: '16px' }}>{formatTime(record.total_talk_seconds)}</Text>
                        <Text type="secondary" style={{ fontSize: '10px' }}>T. HABLADO</Text>
                    </Space>
                </Space>
            )
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 120,
            render: (_, record) => {
                let extension = null;
                try {
                    if (record.sip_extension) {
                        const sipData = JSON.parse(record.sip_extension);
                        extension = sipData.extension;
                    }
                } catch (e) {
                    extension = record.sip_extension;
                }

                const canCallAgent = canGlobalCall && extension && ['online', 'available', 'wrapup'].includes(record.status);
                const canMonitorAgent = canGlobalCall && extension && record.status === 'incall';

                const monitorItems = [
                    {
                        key: 'listen',
                        label: 'Escuchar (*98)',
                        icon: <CustomerServiceOutlined />,
                        onClick: () => {
                            message.info(`Escuchando extensión ${extension}...`);
                            makeCall(`*98${extension}`, `Escucha a ${record.name}`);
                        }
                    },
                    {
                        key: 'whisper',
                        label: 'Susurrar (*99)',
                        icon: <MessageOutlined />,
                        onClick: () => {
                            message.info(`Susurrando a extensión ${extension}...`);
                            makeCall(`*99${extension}`, `Susurro a ${record.name}`);
                        }
                    }
                ];

                return (
                    <Space size="small">
                        <Tooltip title={!extension ? "Sin extensión" : (!canCallAgent ? "Agente no disponible para llamar" : "Llamar al agente")}>
                            <Button
                                type="primary"
                                shape="circle"
                                size="small"
                                icon={<PhoneOutlined />}
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                disabled={!canCallAgent}
                                onClick={() => {
                                    message.info(`Llamando a extensión ${extension}...`);
                                    makeCall(extension);
                                }}
                            />
                        </Tooltip>

                        {canListen && (
                            <Dropdown
                                menu={{ items: monitorItems }}
                                trigger={['click']}
                                disabled={!canMonitorAgent}
                            >
                                <Tooltip title={!canMonitorAgent ? "Solo disponible en llamada" : "Monitorear agente"}>
                                    <Button
                                        shape="circle"
                                        size="small"
                                        icon={<CustomerServiceOutlined />}
                                        style={{
                                            color: canMonitorAgent ? '#1890ff' : '#bfbfbf',
                                            borderColor: canMonitorAgent ? '#1890ff' : '#d9d9d9'
                                        }}
                                        disabled={!canMonitorAgent}
                                    />
                                </Tooltip>
                            </Dropdown>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <div className="fade-in">
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Monitoreo de Agentes</Title>
                    <Text type="secondary">Seguimiento en tiempo real de disponibilidad y productividad.</Text>
                </div>
                <div>
                    <Tag
                        icon={<SyncOutlined spin={loading || isUpdating} />}
                        color="blue"
                        className={isUpdating ? 'update-blink' : ''}
                        style={{ transition: 'all 0.3s' }}
                    >
                        Actualizado: {lastUpdate.toLocaleTimeString()}
                    </Tag>
                </div>
            </div>

            <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>

                <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                    <Card size="small" style={{ flex: 1, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary" style={{ color: '#52c41a', fontSize: '12px', fontWeight: 'bold' }}>DISPONIBLES</Text>
                            <Title level={2} style={{ margin: 0, color: '#389e0d' }}>{data.filter(a => a.status === 'available').length}</Title>
                        </Space>
                    </Card>
                    <Card size="small" style={{ flex: 1, background: '#fff1f0', border: '1px solid #ffa39e' }}>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary" style={{ color: '#f5222d', fontSize: '12px', fontWeight: 'bold' }}>OCUPADOS</Text>
                            <Title level={2} style={{ margin: 0, color: '#cf1322' }}>{data.filter(a => ['incall', 'ringing', 'wrapup'].includes(a.status)).length}</Title>
                        </Space>
                    </Card>
                    <Card size="small" style={{ flex: 1, background: '#fffbe6', border: '1px solid #ffe58f' }}>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary" style={{ color: '#faad14', fontSize: '12px', fontWeight: 'bold' }}>EN PAUSA</Text>
                            <Title level={2} style={{ margin: 0, color: '#d48806' }}>{data.filter(a => a.status === 'break').length}</Title>
                        </Space>
                    </Card>
                    <Card size="small" style={{ flex: 1, background: '#f0f5ff', border: '1px solid #adc6ff' }}>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary" style={{ color: '#2f54eb', fontSize: '12px', fontWeight: 'bold' }}>TOTAL AGENTES</Text>
                            <Title level={2} style={{ margin: 0, color: '#1d39c4' }}>{data.length}</Title>
                        </Space>
                    </Card>
                </div>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    size="middle"
                    className="custom-table"
                />
            </Card>
        </div>
    );
};

export default AgentsPage;
